import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import BottomTabNav from '../shared/components/BottomTabNav';
import { dataService } from '../services/dataService';
import './CustomerCheckout.css';
import '../styles/LoadingError.css';
import { formatCurrency, getUserCurrency, formatCurrencySync } from '../shared/utils/currency';
import useCurrencyCache from '../hooks/useCurrencyCache';

const CustomerCheckout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { items: cartItems, getCartTotal, clearCart } = useCart();
  
  // Use currency cache hook
  const { formatCurrency: formatCurrencyCached } = useCurrencyCache();
  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [deliveryFee, setDeliveryFee] = useState(299); // Default delivery fee
  const [taxRate, setTaxRate] = useState(8.00); // Default tax rate (8%)
  
  // Form states
  const [deliveryDetails, setDeliveryDetails] = useState({
    name: '',
    phone: '',
    address: '',
    city: '',
    zipCode: '',
    deliveryInstructions: ''
  });
  
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [orderNotes, setOrderNotes] = useState('');

  // Calculate totals
  const subtotal = getCartTotal();
  const tax = subtotal * (taxRate / 100); // Use dynamic tax rate
  const total = subtotal + tax + deliveryFee;

  // Fetch delivery fee and tax rate from shop/vendor/pharmacy
  useEffect(() => {
    const fetchVendorSettings = async () => {
      if (!cartItems || cartItems.length === 0) return;
      
      try {
        const firstItem = cartItems[0];
        let fee = 299; // Default fallback
        let tax = 8.00; // Default tax rate fallback
        
        if (firstItem.type === 'food') {
          // Get vendor's delivery fee and tax rate
          const foodItems = await dataService.getFoodItems();
          const foodItem = foodItems.find(item => item.id === firstItem.id);
          if (foodItem?.vendor_id) {
            const vendors = await dataService.getVendors();
            const vendor = vendors.find(v => v.id === foodItem.vendor_id);
            if (vendor?.delivery_fee) {
              fee = parseFloat(vendor.delivery_fee);
            }
            if (vendor?.tax_rate !== undefined && vendor?.tax_rate !== null) {
              tax = parseFloat(vendor.tax_rate);
            }
          }
        } else if (firstItem.type === 'grocery') {
          // Get shop's delivery fee and tax rate
          const groceryItems = await dataService.getGroceryItems();
          const groceryItem = groceryItems.find(item => item.id === firstItem.id);
          if (groceryItem?.shop_id) {
            const shops = await dataService.getShops();
            const shop = shops.find(s => s.id === groceryItem.shop_id);
            if (shop?.delivery_fee) {
              fee = parseFloat(shop.delivery_fee);
            }
            if (shop?.tax_rate !== undefined && shop?.tax_rate !== null) {
              tax = parseFloat(shop.tax_rate);
            }
          }
        } else if (firstItem.type === 'pharmacy') {
          // Get pharmacy's delivery fee and tax rate
          const pharmacyItems = await dataService.getPharmacyItems();
          const pharmacyItem = pharmacyItems.find(item => item.id === firstItem.id);
          if (pharmacyItem?.pharmacy_id) {
            const pharmacies = await dataService.getPharmacies();
            const pharmacy = pharmacies.find(p => p.id === pharmacyItem.pharmacy_id);
            if (pharmacy?.delivery_fee) {
              fee = parseFloat(pharmacy.delivery_fee);
            }
            if (pharmacy?.tax_rate !== undefined && pharmacy?.tax_rate !== null) {
              tax = parseFloat(pharmacy.tax_rate);
            }
          }
        }
        
        setDeliveryFee(fee);
        setTaxRate(tax);
      } catch (error) {
        console.error('Error fetching vendor settings:', error);
        // Keep default values on error
      }
    };
    
    fetchVendorSettings();
  }, [cartItems]);

  const handleDeliveryInputChange = (e) => {
    const { name, value } = e.target;
    setDeliveryDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNextStep = () => {
    if (currentStep === 1 && !deliveryDetails.name.trim()) {
      alert('Please enter your name');
      return;
    }
    if (currentStep === 1 && !deliveryDetails.phone.trim()) {
      alert('Please enter your phone number');
      return;
    }
    if (currentStep === 1 && !deliveryDetails.address.trim()) {
      alert('Please enter your delivery address');
      return;
    }
    
    setCurrentStep(prev => prev + 1);
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handlePlaceOrder = async () => {
    if (!deliveryDetails.name || !deliveryDetails.phone || !deliveryDetails.address) {
      alert('Please fill in all required delivery details');
      return;
    }

    setIsProcessing(true);
    
    console.log('Starting order placement...');
    console.log('Cart items:', cartItems);
    console.log('Delivery details:', deliveryDetails);
    
    try {
      // Get authenticated customer or create one with proper auth_uid
      let customer;
      try {
        // First, try to get current authenticated user
        const { authService } = await import('../config/supabase');
        const authUser = await authService.getCurrentUser();
        
        if (authUser?.id || authUser?.email) {
          // Prefer auth UID to avoid duplicates; fallback to email
          customer = await dataService.getCustomerByAuthUid(authUser.id);
          if (!customer && authUser?.email) {
            customer = await dataService.getCustomerByEmail(authUser.email);
          }
          
          if (!customer) {
            // Create customer record for authenticated user
            const customerData = {
              auth_uid: authUser.id, // Use actual auth UID
              name: deliveryDetails.name || authUser.user_metadata?.name || authUser.email.split('@')[0],
              email: authUser.email,
              phone: deliveryDetails.phone || authUser.user_metadata?.phone,
              address: deliveryDetails.address,
              city: deliveryDetails.city,
              postal_code: deliveryDetails.zipCode
            };
            customer = await dataService.createCustomer(customerData);
            console.log('Created customer record for authenticated user:', customer);
          }
        } else {
          // No authenticated user - create guest customer
          const customerData = {
            auth_uid: 'guest-' + Date.now(), // Generate unique guest ID
            name: deliveryDetails.name,
            email: `${deliveryDetails.name.toLowerCase().replace(/[^a-z0-9]/g, '.')}@guest.example.com`,
            phone: deliveryDetails.phone,
            address: deliveryDetails.address,
            city: deliveryDetails.city,
            postal_code: deliveryDetails.zipCode
          };
          customer = await dataService.createCustomer(customerData);
          console.log('Created guest customer record:', customer);
        }
      } catch (err) {
        console.error('Error with customer creation:', err);
        // Fallback: use existing customer from database
        const customers = await dataService.getCustomers();
        customer = customers[0];
      }

      // Get vendor_id from first cart item (assuming all items are from same vendor)
      const firstItem = cartItems[0];
      let vendorId = null;
      let vendorCurrency = 'PKR'; // Default currency
      
      console.log('First cart item:', firstItem);
      
      if (firstItem.type === 'food') {
        // For food items, get vendor_id from food_items table
        const foodItems = await dataService.getFoodItems();
        const foodItem = foodItems.find(item => item.id === firstItem.id);
        vendorId = foodItem?.vendor_id;
        console.log('Food item found:', foodItem, 'Vendor ID:', vendorId);
        
        // Get vendor currency
        if (vendorId) {
          const vendors = await dataService.getVendors();
          const vendor = vendors.find(v => v.id === vendorId);
          vendorCurrency = vendor?.currency || 'PKR';
          console.log('Vendor currency:', vendorCurrency);
        }
      } else if (firstItem.type === 'grocery') {
        // For grocery items, map to shop_id
        const groceryItems = await dataService.getGroceryItems();
        const groceryItem = groceryItems.find(item => item.id === firstItem.id);
        vendorId = groceryItem?.shop_id || null;
        console.log('Grocery item found:', groceryItem, 'Shop ID:', vendorId);
        
        // Get shop currency
        if (vendorId) {
          const shops = await dataService.getShops();
          const shop = shops.find(s => s.id === vendorId);
          vendorCurrency = shop?.currency || 'PKR';
          console.log('Shop currency:', vendorCurrency);
        }
      } else if (firstItem.type === 'pharmacy') {
        // For pharmacy items, map to pharmacy_id
        const pItems = await dataService.getPharmacyItems();
        const pItem = pItems.find(item => item.id === firstItem.id);
        vendorId = pItem?.pharmacy_id || null;
        console.log('Pharmacy item found:', pItem, 'Pharmacy ID:', vendorId);
        
        // Get pharmacy currency
        if (vendorId) {
          const pharmacies = await dataService.getPharmacies();
          const pharmacy = pharmacies.find(p => p.id === vendorId);
          vendorCurrency = pharmacy?.currency || 'PKR';
          console.log('Pharmacy currency:', vendorCurrency);
        }
      } else if (firstItem.type === 'clothing') {
        // For clothing items, get store_id from clothing_items table
        const clothingItems = await dataService.getClothingItems();
        const clothingItem = clothingItems.find(item => item.id === firstItem.id);
        vendorId = clothingItem?.store_id || null;
        console.log('Clothing item found:', clothingItem, 'Store ID:', vendorId);
        
        // Get clothing store currency
        if (vendorId) {
          const clothingStores = await dataService.getClothingStores();
          const clothingStore = clothingStores.find(s => s.id === vendorId);
          vendorCurrency = clothingStore?.currency || 'PKR';
          console.log('Clothing store currency:', vendorCurrency);
        }
      } else if (firstItem.type === 'accessories') {
        // For accessories items, get store_id from accessories_items table
        const accessoriesItems = await dataService.getAccessoriesItems();
        const accessoriesItem = accessoriesItems.find(item => item.id === firstItem.id);
        vendorId = accessoriesItem?.store_id || null;
        console.log('Accessories item found:', accessoriesItem, 'Store ID:', vendorId);
        
        // Get accessories store currency
        if (vendorId) {
          const accessoriesStores = await dataService.getAccessoriesStores();
          const accessoriesStore = accessoriesStores.find(s => s.id === vendorId);
          vendorCurrency = accessoriesStore?.currency || 'PKR';
          console.log('Accessories store currency:', vendorCurrency);
        }
      } else if (firstItem.type === 'electronics') {
        // For electronics items, get store_id from electronics_items table
        const electronicsItems = await dataService.getElectronicsItems();
        const electronicsItem = electronicsItems.find(item => item.id === firstItem.id);
        vendorId = electronicsItem?.store_id || null;
        console.log('Electronics item found:', electronicsItem, 'Store ID:', vendorId);
        
        // Get electronics store currency
        if (vendorId) {
          const electronicsStores = await dataService.getElectronicsStores();
          const electronicsStore = electronicsStores.find(s => s.id === vendorId);
          vendorCurrency = electronicsStore?.currency || 'PKR';
          console.log('Electronics store currency:', vendorCurrency);
        }
      }
      
      if (!vendorId) {
        throw new Error('Could not determine vendor for this order');
      }

      // Create order
      const orderData = {
        customer_id: customer.id,
        vendor_id: firstItem.type === 'food' ? vendorId : null,
        order_number: `ORD-${Date.now()}`,
        total: total,
        delivery_fee: deliveryFee,
        tax: tax,
        final_total: total,
        currency: vendorCurrency, // Add currency from vendor/shop/pharmacy
        status: 'pending',
        delivery_address: deliveryDetails.address,
        delivery_city: deliveryDetails.city,
        delivery_postal_code: deliveryDetails.zipCode,
        delivery_phone: deliveryDetails.phone,
        delivery_notes: orderNotes || deliveryDetails.deliveryInstructions,
        payment_method: paymentMethod,
        payment_status: 'pending'
      };
      // Persist shop_id, pharmacy_id for different order types
      // Note: clothing, accessories, and electronics stores are in separate tables,
      // so we don't set shop_id for them. The vendor panel filters orders by order_items anyway.
      if (firstItem.type === 'grocery') orderData.shop_id = vendorId;
      if (firstItem.type === 'pharmacy') orderData.pharmacy_id = vendorId;
      // For clothing, accessories, and electronics, we don't set shop_id since they're not in the shops table
      // The vendor panel will still find these orders by filtering order_items

      console.log('Creating order with data:', orderData);
      const order = await dataService.createOrder(orderData);
      console.log('Order created successfully:', order);

      // Create order items using only generic fields
      // Note: The order_items table doesn't have foreign key columns (food_item_id, etc.)
      // So we store all items using generic fields: item_type, item_id, item_name, etc.
      const orderItems = cartItems.map(item => ({
        order_id: order.id,
        item_name: item.name,
        item_image: item.image || item.image_url || '',
        item_price: item.price,
        quantity: item.quantity,
        variations: item.variations || '[]',
        subtotal: item.price * item.quantity,
        item_type: item.type, // Store item type: 'food', 'grocery', 'pharmacy', 'clothing', 'accessories', 'electronics'
        item_id: item.id // Store item ID for reference (not a foreign key)
      }));

      console.log('Creating order items:', orderItems);
      await dataService.createOrderItems(orderItems);
      console.log('Order items created successfully');

      setIsProcessing(false);
      
      // Navigate to order confirmation
      navigate('/order-confirmation', {
        state: {
          orderDetails: {
            orderId: order.id,
            orderNumber: `ORD-${order.id.toString().padStart(3, '0')}`,
            items: cartItems,
            delivery: deliveryDetails,
            payment: paymentMethod,
            subtotal: subtotal,
            tax: tax,
            taxRate: taxRate,
            deliveryFee: deliveryFee,
            total: total,
            estimatedDelivery: new Date(Date.now() + 45 * 60000).toLocaleTimeString()
          }
        }
      });
      clearCart();
    } catch (error) {
      console.error('Error placing order:', error);
      setIsProcessing(false);
      alert(`Failed to place order: ${error.message || 'Please try again.'}`);
    }
  };

  const renderStep1 = () => (
    <div className="checkout-step">
      <div className="step-header">
        <div className="step-number active">1</div>
        <h2>Delivery Details</h2>
      </div>
      
      <div className="form-section">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name">Full Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={deliveryDetails.name}
              onChange={handleDeliveryInputChange}
              placeholder="Enter your full name"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="phone">Phone Number *</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={deliveryDetails.phone}
              onChange={handleDeliveryInputChange}
              placeholder="Enter your phone number"
              required
            />
          </div>
        </div>
        
        <div className="form-group">
          <label htmlFor="address">Delivery Address *</label>
          <input
            type="text"
            id="address"
            name="address"
            value={deliveryDetails.address}
            onChange={handleDeliveryInputChange}
            placeholder="Enter your complete address"
            required
          />
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="city">City</label>
            <input
              type="text"
              id="city"
              name="city"
              value={deliveryDetails.city}
              onChange={handleDeliveryInputChange}
              placeholder="Enter your city"
            />
          </div>
          <div className="form-group">
            <label htmlFor="zipCode">ZIP Code</label>
            <input
              type="text"
              id="zipCode"
              name="zipCode"
              value={deliveryDetails.zipCode}
              onChange={handleDeliveryInputChange}
              placeholder="Enter ZIP code"
            />
          </div>
        </div>
        
        <div className="form-group">
          <label htmlFor="deliveryInstructions">Delivery Instructions</label>
          <textarea
            id="deliveryInstructions"
            name="deliveryInstructions"
            value={deliveryDetails.deliveryInstructions}
            onChange={handleDeliveryInputChange}
            placeholder="Any special instructions for delivery?"
            rows="3"
          />
        </div>
      </div>
      
      <div className="step-actions">
        <button className="btn-secondary" onClick={() => navigate('/cart')}>
          Back to Cart
        </button>
        <button className="btn-primary" onClick={handleNextStep}>
          Continue to Payment
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="checkout-step">
      <div className="step-header">
        <div className="step-number active">2</div>
        <h2>Payment Method</h2>
      </div>
      
      <div className="payment-section">
        <div className="payment-option">
          <input
            type="radio"
            id="cash"
            name="payment"
            value="cash"
            checked={paymentMethod === 'cash'}
            onChange={(e) => setPaymentMethod(e.target.value)}
          />
          <label htmlFor="cash" className="payment-label">
            <div className="payment-icon">💵</div>
            <div className="payment-info">
              <h3>Cash on Delivery</h3>
              <p>Pay when you receive your order</p>
            </div>
          </label>
        </div>
        
        <div className="payment-option">
          <input
            type="radio"
            id="card"
            name="payment"
            value="card"
            checked={paymentMethod === 'card'}
            onChange={(e) => setPaymentMethod(e.target.value)}
          />
          <label htmlFor="card" className="payment-label">
            <div className="payment-icon">💳</div>
            <div className="payment-info">
              <h3>Credit/Debit Card</h3>
              <p>Secure online payment</p>
            </div>
          </label>
        </div>
      </div>
      
      <div className="form-group">
        <label htmlFor="orderNotes">Order Notes (Optional)</label>
        <textarea
          id="orderNotes"
          value={orderNotes}
          onChange={(e) => setOrderNotes(e.target.value)}
          placeholder="Any special requests or notes for your order?"
          rows="3"
        />
      </div>
      
      <div className="step-actions">
        <button className="btn-secondary" onClick={handlePrevStep}>
          Back
        </button>
        <button className="btn-primary" onClick={handlePlaceOrder}>
          Place Order
        </button>
      </div>
    </div>
  );

  if (cartItems.length === 0) {
    return (
      <div className="App">
        <div className="checkout-container">
          <div className="empty-checkout">
            <div className="empty-checkout-animation">
              <svg width="200" height="200" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Shopping Bag */}
                <path d="M60 70 L60 160 C60 165 62 170 67 170 L133 170 C138 170 140 165 140 160 L140 70 Z" fill="#5B3EF0" opacity="0.1" stroke="#5B3EF0" strokeWidth="3">
                  <animate attributeName="opacity" values="0.1;0.2;0.1" dur="2s" repeatCount="indefinite"/>
                </path>
                
                {/* Bag Handles */}
                <path d="M 75 70 Q 75 45 100 45 Q 125 45 125 70" stroke="#5B3EF0" strokeWidth="3" fill="none" strokeLinecap="round"/>
                
                {/* Lock Icon */}
                <circle cx="100" cy="110" r="15" stroke="#5B3EF0" strokeWidth="2" fill="none" opacity="0.3"/>
                <rect x="95" y="105" width="10" height="15" fill="#5B3EF0" opacity="0.3" rx="1"/>
                <circle cx="100" cy="115" r="2" fill="#5B3EF0"/>
                
                {/* Credit Card */}
                <rect x="140" y="90" width="40" height="25" rx="3" fill="#5B3EF0" opacity="0.2">
                  <animate attributeName="x" values="140;145;140" dur="2s" repeatCount="indefinite"/>
                </rect>
                <line x1="145" y1="98" x2="175" y2="98" stroke="#5B3EF0" strokeWidth="1.5" opacity="0.4"/>
                <line x1="145" y1="104" x2="165" y2="104" stroke="#5B3EF0" strokeWidth="1" opacity="0.3"/>
                
                {/* Floating Coins */}
                <circle cx="50" cy="100" r="8" fill="#5B3EF0" opacity="0.2">
                  <animate attributeName="cy" values="100;95;100" dur="2.5s" repeatCount="indefinite"/>
                  <animate attributeName="opacity" values="0.2;0.4;0.2" dur="2.5s" repeatCount="indefinite"/>
                </circle>
                <text x="47" y="105" fontSize="10" fill="#fff" opacity="0.8">$</text>
                
                <circle cx="150" cy="60" r="6" fill="#5B3EF0" opacity="0.15">
                  <animate attributeName="cy" values="60;55;60" dur="3s" repeatCount="indefinite"/>
                  <animate attributeName="opacity" values="0.15;0.3;0.15" dur="3s" repeatCount="indefinite"/>
                </circle>
                <text x="148" y="64" fontSize="8" fill="#fff" opacity="0.8">$</text>
              </svg>
            </div>
            <h2>Your cart is empty</h2>
            <p>Add some items to your cart to proceed with checkout</p>
            <button className="btn-primary" onClick={() => navigate('/menu')}>
              🍽️ Browse Menu
            </button>
          </div>
        </div>
        <BottomTabNav />
      </div>
    );
  }

  return (
    <div className="App">
      <div className="checkout-container">
        <div className="checkout-header">
          <h1>Checkout</h1>
        </div>

        <div className="checkout-content">
          <div className="checkout-main">
            {currentStep === 1 ? renderStep1() : renderStep2()}
          </div>
          
          <div className="checkout-sidebar">
            <div className="order-summary">
              <h3>Order Summary</h3>
              <div className="summary-items">
                {cartItems.map((item) => (
                  <div key={item.id} className="summary-item">
                    <span className="item-name">{item.name} × {item.quantity}</span>
                    <span className="item-price">{formatCurrencyCached(item.price * item.quantity, getUserCurrency())}</span>
                  </div>
                ))}
              </div>
              
              <div className="summary-totals">
                <div className="summary-row">
                  <span>Subtotal</span>
                  <span>{formatCurrencyCached(subtotal, getUserCurrency())}</span>
                </div>
                <div className="summary-row">
                  <span>Tax ({taxRate}%)</span>
                  <span>{formatCurrencyCached(tax, getUserCurrency())}</span>
                </div>
                <div className="summary-row">
                  <span>Delivery Fee</span>
                  <span>{formatCurrencyCached(deliveryFee, getUserCurrency())}</span>
                </div>
                <div className="summary-row total">
                  <span>Total</span>
                  <span>{formatCurrencyCached(total, getUserCurrency())}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {isProcessing && (
        <div className="processing-overlay">
          <div className="processing-modal">
            <div className="processing-spinner"></div>
            <h3>Processing Your Order...</h3>
            <p>Please wait while we confirm your order details</p>
          </div>
        </div>
      )}
      
      <BottomTabNav />
    </div>
  );
};

export default CustomerCheckout;
